
import { sha512 } from 'crypto-hash';
import Page from '@root/common/base/Page';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { RelatedArticle } from '@root/common/types/fragment';
import ArticleService from '@root/modules/article/services/Article.service';

import ArticleSkeletonLoader from '@root/modules/article/components/ArticleSkeletonLoader.vue';
import articleJsonld from '@root/modules/article/utils/articleJsonld';

import ArticleContent from '@root/modules/article/components/ArticleContent.vue';
import ArticleContentVideo from '@root/modules/article/components/ArticleContentVideo.vue';
import ArticleContentTilda from '@root/modules/article/components/ArticleContentTilda.vue';
import { getArticleMeta } from '@root/modules/article/utils/articleMeta';

import getArticleAdsSettings from '@root/modules/article/utils/getArticleAdsSettings';
import isArticleMultiPage from '@root/modules/article/utils/isArticleMultipage';
import getFakeArticleUrl from '@root/modules/article/utils/getFakeArticleUrl';

import getArticlePath from '@root/modules/article/utils/getArticlePath';
import getArticleUrl, { buildArticleCommentsUrl as getArticleCommentsUrl } from '@root/modules/article/utils/buildArticleUrl';

import isTildaArticle from '@root/modules/article/utils/isTildaArticle';

import { getArticleRelatedHeadlines } from '@root/modules/article/utils/getArticleRelatedHeadlines';
import { fillArticleData } from '@root/modules/article/utils/fillArticleData';
import { doesArticleBaseDataExist } from '@root/modules/article/utils/doesArticleBaseDataExist';
import { getArticlePaywallState } from '@root/modules/article/utils/getArticlePaywallState';
import { shouldEnableCustomerAccessForArticle, shouldDisableCustomerAccessForArticle } from '@root/modules/article/utils/checkCustomerAccessForArticle';

import CustomerDataService from '@root/modules/customer/services/CustomerData.service';
import articleConfig from '@root/modules/article/config/article.config';

interface Data {
  article: ArticleData | null;
  articleRelatedHeadlines: RelatedArticle[];
  requestFailed: boolean;
  isArticleFetching: boolean;
  isRefetched: boolean;
  shouldBeRefetched: boolean;
  readingHistoryEntryCreated: boolean;
  articleObserver: IntersectionObserver | null;
  lazyArticlesObserver: MutationObserver | null;
  hashedCanonical: string;
  gaaEntitlement: {
    timestamp: number;
    value: string;
  };
}

interface Props {
  articleId: number;
  initialArticle: boolean;
}

interface Computed {
  localArticle: ArticleData | null;
  customerHasAccess: boolean;
  customerToken: string | null;
  isFetchPending: boolean;
  isVideoArticle: boolean;
  isMultimedijaArticle: boolean;
  isTildaArticle: boolean;
}

interface Methods {
  handleArticleLoad: (articleId: string) => void;
  refetchArticle: (doRefetch: boolean) => void;
  handleArticleRequestFailure: ({ article, access, customerToken }: { article: ArticleData; access: boolean; customerToken: string | null }) => void;
  handleRedirect: () => void;
  createReadingHistoryEntry: (articleId: number) => void;
  handleArticleObservers: () => void;
}

export default Page.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleSkeletonLoader,
    ArticleContent,
    ArticleContentVideo,
    ArticleContentTilda,
  },
  validate({ params }) {
    return /^\d+$/.test(params.id) && /^[a-z0-9-]+$/.test(params.slug);
  },
  props: {
    articleId: {
      type: Number,
      required: true,
    },
    initialArticle: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      article: this.$store.getters['article/getTempData']?.article || null,
      articleRelatedHeadlines: [],
      meta: null,
      requestFailed: false,
      isArticleFetching: true,
      isRefetched: false,
      shouldBeRefetched: false,
      readingHistoryEntryCreated: false,
      articleObserver: null,
      lazyArticlesObserver: null,
      // NB! Value has side effect of adding Prefer header to CAv3 requests
      hashedCanonical: '',
      gaaEntitlement: {
        timestamp: 0,
        value: '',
      },
    };
  },
  async fetch() {
    this.isArticleFetching = true;

    const channelConfig = this.$channelConfig('config');
    const { externalId: channelId, application, lang } = channelConfig.settings;
    const { articleId, customerToken, $route } = this;
    const preferHeader = Boolean(process.client && this.hashedCanonical);

    // Check if customer has rights to read the article
    try {
      const articleBaseDataExists = doesArticleBaseDataExist(this.article);
      const variables = {
        id: articleId,
        language: lang.toUpperCase(),
        channelId,
        customerToken,
        application,
        preview: $route.query.preview as string,
        hashedCanonical: this.hashedCanonical,
        settings: {
          requestBody: articleBaseDataExists,
          authorizationHeader: this.article?.content.paywall.enabled ?? true,
          preferHeader,
        },
      };

      const articleService = new ArticleService({ req: this.$ssrContext?.req });
      const articleData = await articleService.fetch({ variables, channelConfig });

      // Handle article not found error
      if (!articleData?.article) {
        articleService.handleError({ message: 'article.error.not_found', statusCode: 404 });
        return;
      }

      const { article, access, requestContext } = articleData;

      const entitlement = requestContext?.response.headers.get('Google-Showcase-Entitlement') || '';
      this.gaaEntitlement = {
        timestamp: Date.now(),
        value: entitlement,
      };

      // Handle short URLs redirect
      const isShortUrl = this.$route.name === 'articleShortUrl';
      if (isShortUrl) {
        const isCommentsUrl = this.$route.path.includes('/diskusija');
        const pathOptions = {
          article,
          domain: this.$channelConfig('settings').domain,
          fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED,
          query: this.$nuxt.context.query,
          route: this.$nuxt.context.route,
        };
        const redirectPath = isCommentsUrl ? getArticleCommentsUrl(pathOptions) : getArticleUrl(pathOptions);
        this.handlePageRedirect(redirectPath);
      }

      // Handle fake article redirect
      if (article.settings.fakeArticle || article.settings.redirectArticle) {
        const fakeArticleUrl = getFakeArticleUrl(article);
        if (fakeArticleUrl) {
          this.$nuxt.context.redirect(302, fakeArticleUrl);
          return;
        }
      }

      if (isArticleMultiPage(article)) {
        article.settings.isMultiPage = true;
      }

      // Fill article data:
      // - SSR request: fill article data with fetched partial article data without reader access
      // - CSR request(initial article request or on token change): update article data with fetched content data with reader access
      // - CSR request(not initial article): fill article data with fetched full article data with or without reader access
      this.article = fillArticleData(article, this.article, { articleBaseDataExists });

      if (this.article) {
        this.handleRedirect();
      }

      if (this.article) {
        this.setPageAdsSettings(getArticleAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article: this.article }));
      }

      // Rewrite isMultipage setting for paid articles at CSR body reload
      const paywall = getArticlePaywallState(this.article);
      if (paywall.enabled && isArticleMultiPage(this.article)) {
        this.article.settings.isMultiPage = true;
      }

      this.$store.commit('article/setActiveArticle', this.article);
      this.$store.commit('category/setCategoryData', article.primaryCategory);

      if (this.initialArticle) {
        this.$store.commit('article/setActiveArticlePath', this.$route.fullPath);
        this.$store.commit('article/setComposerTarget', articleId);
      }

      // Set related headlines for article bottom section
      if (this.articleRelatedHeadlines.length === 0) {
        this.articleRelatedHeadlines = getArticleRelatedHeadlines(article);
      }

      if (articleBaseDataExists) {
        this.isArticleFetching = false;
        return;
      }

      this.handleArticleRequestFailure({ article, access, customerToken });
    } catch (e) {
      if (this.initialArticle) {
        this.handlePageError(e);
        return;
      }

      this.$emit('article-loaded', false);
    }

    if (process.client) {
      this.$nextTick(() => {
        this.isArticleFetching = false;
      });
    }
  },
  head() {
    if (this.article) {
      const meta = getArticleMeta({ $channelConfig: this.$channelConfig, article: this.article, path: this.$route.path });

      // Inject ld+json into head
      const jsonLd = articleJsonld(this.$channelConfig('meta').base, this.article);
      meta.script = [{ hid: 'article-ld-json', type: 'application/ld+json', json: jsonLd }];

      if (articleConfig.noindexArticles && this.article.id && articleConfig.noindexArticles.includes(this.article.id)) {
        meta.robots = 'noindex, nofollow';
      }
      return {
        title: meta.title,
        meta: meta.data,
        style: meta.style,
        link: meta.link,
        script: meta.script,
        robots: meta.robots,
        __dangerouslyDisableSanitizers: ['style'],
      };
    }
    return {};
  },
  computed: {
    localArticle() {
      return this.article;
    },
    customerHasAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    customerToken() {
      return this.$store.state.piano.token;
    },
    isFetchPending() {
      return this.$fetchState?.pending;
    },
    isVideoArticle() {
      return this.article?.primaryCategory.channel.url === 'www.delfi.lt/tv';
    },
    isMultimedijaArticle() {
      const { id } = this.$channelConfig('settings');
      return id === 'multimedija';
    },
    isTildaArticle() {
      const customCategorySettings = this.$channelConfig('page').category?.component?.header;
      return isTildaArticle(this.$route, customCategorySettings);
    },
  },
  watch: {
    requestFailed(reFetch) {
      const doRefetch = reFetch && process.client;
      this.refetchArticle(doRefetch);
    },
    customerHasAccess(access: boolean) {
      const paywall = getArticlePaywallState(this.article);

      if (shouldEnableCustomerAccessForArticle(access, paywall) || shouldDisableCustomerAccessForArticle(access, paywall)) {
        this.refetchArticle(true);
      }
    },
    customerToken() {
      if (!this.articleObserver) {
        this.handleArticleObservers();
      }
    },
    isFetchPending(isPending) {
      if (!isPending && this.shouldBeRefetched) {
        this.$nextTick(() => {
          this.shouldBeRefetched = false;
          this.$fetch();
        });
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();
    this.resetPageHeaderBottomNavigation();

    next();
  },
  beforeDestroy() {
    this.$store.commit('article/setActiveArticle', null);
    this.$store.commit('category/setCategoryData', null);

    if (this.articleObserver) {
      this.articleObserver.disconnect();
    }

    if (this.lazyArticlesObserver) {
      this.lazyArticlesObserver.disconnect();
    }
  },
  beforeMount() {
    if (this.initialArticle && !this.isMultimedijaArticle) {
      window.scrollTo(0, 0);
    }
  },
  async mounted() {
    const { application } = this.$channelConfig('settings');

    // Refetch first article for:
    // 1. article for allowed IP-s
    // 2. for IOS applications to by pass apple review rules
    const paywall = getArticlePaywallState(this.article);
    const userHasAccess = this.$store.state.piano.access.channelAccess;
    const doRefetch = (this.initialArticle && application.specialFeatures.forceArticleRefetch) || shouldEnableCustomerAccessForArticle(userHasAccess, paywall);

    this.refetchArticle(doRefetch);

    // 3. Refetch article for GAA bullshit
    const handleGaaArticleUnlock = async (): Promise<string> => {
      const canonicalUrl = window.location.host + window.location.pathname;
      this.hashedCanonical = await sha512(canonicalUrl);

      return new Promise((resolve) => {
        this.$watch('gaaEntitlement', (newValue) => {
          resolve(newValue.value);
        });

        this.refetchArticle(true);
      });
    };

    const handleGaaArticleLock = async () => {
      this.hashedCanonical = '';

      this.refetchArticle(true);
    };

    this.$gaa.initGaa(handleGaaArticleUnlock, handleGaaArticleLock);

    if (!this.isRefetched) {
      this.isArticleFetching = false;
    }
  },
  methods: {
    handleArticleLoad(articleId) {
      this.$emit('article-loaded', !!articleId);
    },
    refetchArticle(doRefetch) {
      if (!!this.$fetch && doRefetch && !this.$route.query.preview) {
        /*
          this.$fetch() can't be called when it's already pending
          in edge cases, when article has to be refetched 2 times
          (Logged in from iOS app. 1st refetch - special feature, 2nd - customerToken watcher)
          2nd time will be not called at all, because 1st is already pending
        */
        if (!this.isFetchPending) {
          this.isRefetched = true;

          this.$nextTick(() => {
            this.$fetch();
          });
        } else {
          this.shouldBeRefetched = true;
        }
      }
    },
    handleArticleRequestFailure({ article, access, customerToken }) {
      if (process.server) {
        return;
      }
      // Fallback check if article request fails
      const emptyBody = article.content.paywall.enabled && access && !(article.content.body.content.length > 0);
      const { externalId: channelId } = this.$channelConfig('settings');
      const { articleId } = this;

      if (emptyBody && !this.requestFailed) {
        this.requestFailed = true;
        this.$sentry.captureException(`Article fetch with no body - articleId: ${articleId}, channelId: ${channelId}, userToken: ${customerToken}`, {
          contexts: { vue: { componentName: 'Article' } },
          tags: { 'process.server': process.server ? 'server' : 'client' },
        });
      }
    },
    handleRedirect() {
      if (!this.article) {
        return;
      }

      const redirectPath = getArticlePath(this.article, {
        domain: this.$channelConfig('settings').domain,
        fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED,
        query: this.$nuxt.context.query,
        route: this.$nuxt.context.route,
      });

      this.handlePageRedirect(redirectPath);
    },
    async createReadingHistoryEntry(articleId) {
      if (!this.customerToken || this.readingHistoryEntryCreated) {
        return;
      }

      try {
        const customerDataService = new CustomerDataService();
        customerDataService.setCustomerToken(this.$store.state.piano.token);
        await customerDataService.createReadingHistoryEntry({ articleId });
        this.readingHistoryEntryCreated = true;
      } catch (e) {
        this.$sentry.captureException(e, {
          tags: { 'process.type': process.server ? 'server' : 'client' },
        });
      } finally {
        this.readingHistoryEntryCreated = false;
      }
    },
    handleArticleObservers() {
      const intersectionCallback = (entries: any) => {
        entries.forEach((entry: any) => {
          if (entry.intersectionRatio > 0) {
            const element = entry.target as unknown as HTMLElement;
            const articleId = Number(element.id.replace('article-', ''));
            this.createReadingHistoryEntry(articleId);
            this.articleObserver?.unobserve(entry.target);
          }
        });
      };

      this.articleObserver = new IntersectionObserver(intersectionCallback);
      this.articleObserver.observe(document.querySelector('.article') as Element);

      const mutationCallback = (mutations: any) => {
        mutations.forEach((mutation: any) => {
          if (mutation.type === 'childList') {
            Array.prototype.filter.call(mutation.addedNodes, (node) => {
              if (node.childNodes.length > 0 && (node.childNodes[0] as Element)?.classList?.contains('article')) {
                const article = node.childNodes[0] as Element;
                this.articleObserver?.observe(article);
              }
            });
          }
        });
      };

      this.lazyArticlesObserver = new MutationObserver(mutationCallback);
      this.lazyArticlesObserver.observe(document.querySelector('.articles-wrapper') as Element, { childList: true, subtree: true });
    },
  },
});
